/**
 * 嵌入论坛自动 SSO 桥（协议 v1-lite）
 *
 * CommonJS 模块：shakapacker（浏览器 bundle）与 Node test runner 共用，
 * 必须用 .js 扩展名（shakapacker 8.4.0 的 resolve.extensions 不含 .cjs）。
 *
 * 职责：
 * - 监听父页（Adam CommunityPage）的 ADAM_SSO_PROBE / ADAM_SSO_START
 * - 稳定页（turbolinks:load）上报 HOMELAND_SSO_STATE
 * - 授权后 location.replace 进入 /auth/sso 完成自动登录
 * - sessionStorage 60s 冷却防止重定向循环
 *
 * 安全约束：
 * - 仅在 iframe 内且 data-parent-origin 配置合法时工作，否则完全静默
 * - 消息校验 event.origin === parentOrigin && event.source === window.parent
 * - postMessage targetOrigin 固定为精确 parentOrigin
 * - 消息不携带用户资料、完整 URL、query、票据
 */

var CHANNEL = "adam.homeland.community-sso";
var VERSION = 1;
var MESSAGE_SKEW_MS = 30000;
var COOLDOWN_MS = 60000;
var RETURN_PATH_MAX_BYTES = 4096;
var STORAGE_KEY = "adam_community_sso_attempt_v1";
var UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

// ---------- 纯工具（测试直接复用） ----------

function isValidUuid(value) {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

/**
 * 生成 UUID v4：优先 crypto.randomUUID，回退 crypto.getRandomValues 组装；
 * 均不可用时返回 null（调用方放弃参与自动流程，保留手动登录）。
 */
function generateUuid() {
  var cryptoApi =
    typeof globalThis !== "undefined" ? globalThis.crypto : null;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  if (cryptoApi && typeof cryptoApi.getRandomValues === "function") {
    var bytes = cryptoApi.getRandomValues(new Uint8Array(16));
    // 按 RFC 4122 v4 设置版本与变体位
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    var hex = "";
    for (var i = 0; i < bytes.length; i++) {
      var byteHex = bytes[i].toString(16);
      if (byteHex.length < 2) {
        byteHex = "0" + byteHex;
      }
      hex += byteHex;
    }
    return (
      hex.slice(0, 8) +
      "-" +
      hex.slice(8, 12) +
      "-" +
      hex.slice(12, 16) +
      "-" +
      hex.slice(16, 20) +
      "-" +
      hex.slice(20, 32)
    );
  }
  return null;
}

function utf8ByteLength(str) {
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(str).length;
  }
  var bytes = 0;
  for (var i = 0; i < str.length; i++) {
    var code = str.charCodeAt(i);
    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else if (code >= 0xd800 && code <= 0xdbff) {
      bytes += 4;
      i++;
    } else {
      bytes += 3;
    }
  }
  return bytes;
}

/**
 * 从 body data-parent-origin 严格解析父页 origin
 */
function getConfiguredParentOrigin(documentRef) {
  var raw = "";
  try {
    raw = (documentRef.body && documentRef.body.dataset
      ? documentRef.body.dataset.parentOrigin
      : ""
    ).trim();
  } catch (e) {
    return null;
  }
  if (!raw) {
    return null;
  }
  try {
    var url = new URL(raw);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }
    return url.origin;
  } catch (e) {
    return null;
  }
}

/** 稳定页判定：非 loading 且非 turbolinks preview */
function isStablePage(documentRef) {
  return Boolean(
    documentRef.body &&
      documentRef.readyState !== "loading" &&
      !documentRef.documentElement.hasAttribute("data-turbolinks-preview")
  );
}

/**
 * 安全 returnPath：仅站内相对路径
 */
function currentSafeReturnPath(locationRef) {
  var candidate = locationRef.pathname + locationRef.search;
  if (utf8ByteLength(candidate) > RETURN_PATH_MAX_BYTES) {
    return "/";
  }
  if (!candidate.startsWith("/") || candidate.startsWith("//")) {
    return "/";
  }
  if (
    candidate.indexOf("\\") !== -1 ||
    /[\u0000-\u0020\u007f]/.test(candidate)
  ) {
    return "/";
  }
  try {
    var resolved = new URL(candidate, locationRef.origin);
    if (resolved.origin !== locationRef.origin) {
      return "/";
    }
    return resolved.pathname + resolved.search;
  } catch (e) {
    return "/";
  }
}

/** 解析父页消息信封与载荷，非法返回 null */
function parseParentMessage(data, nowMs) {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return null;
  }
  if (data.channel !== CHANNEL || data.version !== VERSION) {
    return null;
  }
  if (data.source !== "adam") {
    return null;
  }
  if (typeof data.type !== "string") {
    return null;
  }
  if (
    typeof data.sentAt !== "number" ||
    !isFinite(data.sentAt) ||
    Math.abs(nowMs - data.sentAt) > MESSAGE_SKEW_MS
  ) {
    return null;
  }
  if (data.type === "ADAM_SSO_PROBE") {
    if (!isValidUuid(data.probeId)) {
      return null;
    }
    return { type: "ADAM_SSO_PROBE", probeId: data.probeId };
  }
  if (data.type === "ADAM_SSO_START") {
    if (!isValidUuid(data.pageId) || !isValidUuid(data.attemptId)) {
      return null;
    }
    if (data.mode !== "automatic" && data.mode !== "user_retry") {
      return null;
    }
    return {
      type: "ADAM_SSO_START",
      pageId: data.pageId,
      attemptId: data.attemptId,
      mode: data.mode,
    };
  }
  return null;
}

// sessionStorage 容错包装
function storageGet(storage, key) {
  if (!storage) {
    return null;
  }
  try {
    return storage.getItem(key);
  } catch (e) {
    return null;
  }
}

function storageSet(storage, key, value) {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(key, value);
  } catch (e) {
    // 内存守卫继续，页面不崩溃
  }
}

function storageRemove(storage, key) {
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(key);
  } catch (e) {
    // 内存守卫继续，页面不崩溃
  }
}

function getDefaultStorage(windowRef) {
  try {
    return windowRef.sessionStorage;
  } catch (e) {
    return null;
  }
}

var NOOP_INSTANCE = {
  onStableLoad: function () {},
};

/**
 * 创建桥实例
 */
function createSsoAutologin(options) {
  var windowRef = options.window;
  var documentRef = options.document;
  var storageRef =
    options.storage === undefined
      ? getDefaultStorage(windowRef)
      : options.storage;
  var now =
    options.now ||
    function () {
      return Date.now();
    };
  var parentOrigin =
    options.parentOrigin !== undefined
      ? options.parentOrigin
      : getConfiguredParentOrigin(documentRef);
  var navigate =
    options.navigate ||
    function (href) {
      windowRef.location.replace(href);
    };
  var postMessage =
    options.postMessage ||
    function (message) {
      windowRef.parent.postMessage(message, parentOrigin);
    };

  var currentPageId = null;
  var memoryAttempt = null;
  var navigatedAttemptId = null;

  function isLogined() {
    return Boolean(documentRef.querySelector('meta[name="current-user"]'));
  }

  function readStoredAttempt() {
    var raw = storageGet(storageRef, STORAGE_KEY);
    if (!raw) {
      return null;
    }
    var parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      parsed = null;
    }
    if (
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed) &&
      parsed.version === 1 &&
      parsed.phase === "started" &&
      isValidUuid(parsed.attemptId) &&
      typeof parsed.startedAt === "number" &&
      isFinite(parsed.startedAt) &&
      typeof parsed.updatedAt === "number" &&
      isFinite(parsed.updatedAt)
    ) {
      return {
        version: 1,
        attemptId: parsed.attemptId,
        phase: "started",
        startedAt: parsed.startedAt,
        updatedAt: parsed.updatedAt,
      };
    }
    // 畸形记录立即清除
    storageRemove(storageRef, STORAGE_KEY);
    return null;
  }

  function clearAttempt() {
    memoryAttempt = null;
    storageRemove(storageRef, STORAGE_KEY);
  }

  function getActiveAttempt() {
    var attempt = memoryAttempt || readStoredAttempt();
    if (!attempt) {
      return null;
    }
    var age = now() - attempt.startedAt;
    if (age >= COOLDOWN_MS || age < 0) {
      clearAttempt();
      return null;
    }
    return attempt;
  }

  function remainingCooldown(attempt) {
    var remaining = COOLDOWN_MS - (now() - attempt.startedAt);
    return Math.min(COOLDOWN_MS, Math.max(0, remaining));
  }

  function saveAttempt(attemptId) {
    var startedAt = now();
    memoryAttempt = {
      version: 1,
      attemptId: attemptId,
      phase: "started",
      startedAt: startedAt,
      updatedAt: startedAt,
    };
    storageSet(storageRef, STORAGE_KEY, JSON.stringify(memoryAttempt));
  }

  /** 构造并上报 Homeland → 父页协议消息 */
  function postHomelandMessage(type, fields) {
    var message = {
      channel: CHANNEL,
      version: VERSION,
      source: "homeland",
      type: type,
      sentAt: now(),
      pageId: currentPageId,
    };
    for (var key in fields) {
      message[key] = fields[key];
    }
    postMessage(message);
  }

  function postStarted(attemptId) {
    postHomelandMessage("HOMELAND_SSO_STARTED", {
      attemptId: attemptId,
    });
  }

  function postRejected(attemptId, reason, retryAfterMs) {
    var clamped = isFinite(retryAfterMs) ? retryAfterMs : 0;
    clamped = Math.min(COOLDOWN_MS, Math.max(0, clamped));
    postHomelandMessage("HOMELAND_SSO_REJECTED", {
      attemptId: attemptId,
      reason: reason,
      retryAfterMs: clamped,
    });
  }

  /** 上报当前稳定页状态 */
  function sendState() {
    if (!currentPageId) {
      return;
    }
    if (isLogined()) {
      var attempt = getActiveAttempt();
      var attemptId = attempt ? attempt.attemptId : null;
      if (attempt) {
        clearAttempt();
      }
      postHomelandMessage("HOMELAND_SSO_STATE", {
        session: "authenticated",
        attemptId: attemptId,
        autoStartAllowed: true,
        retryAfterMs: 0,
      });
      return;
    }
    var active = getActiveAttempt();
    postHomelandMessage("HOMELAND_SSO_STATE", {
      session: "anonymous",
      attemptId: null,
      autoStartAllowed: !active,
      retryAfterMs: active ? remainingCooldown(active) : 0,
    });
  }

  function buildSsoUrl() {
    var target = new URL("/auth/sso", windowRef.location.origin);
    target.searchParams.set("direct_login", "1");
    // /account/sign_in 页省略 return_path，让 controller 使用已有 destination_url cookie
    if (windowRef.location.pathname !== "/account/sign_in") {
      target.searchParams.set("return_path", currentSafeReturnPath(windowRef.location));
    }
    return target.href;
  }

  function handleStart(message) {
    if (!currentPageId) {
      return;
    }
    if (message.pageId !== currentPageId) {
      postRejected(message.attemptId, "stale_page", 0);
      return;
    }
    if (isLogined()) {
      postRejected(message.attemptId, "already_authenticated", 0);
      return;
    }
    // 幂等：同 attemptId 只导航一次，重复 START 最多重发 STARTED
    if (navigatedAttemptId === message.attemptId) {
      postStarted(message.attemptId);
      return;
    }
    // automatic 受 60s 冷却限制；user_retry 可覆盖
    var active = getActiveAttempt();
    if (message.mode === "automatic" && active) {
      postRejected(message.attemptId, "cooldown", remainingCooldown(active));
      return;
    }
    saveAttempt(message.attemptId);
    navigatedAttemptId = message.attemptId;
    postStarted(message.attemptId);
    navigate(buildSsoUrl());
  }

  function handleMessage(event) {
    if (event.origin !== parentOrigin) {
      return;
    }
    if (event.source !== windowRef.parent) {
      return;
    }
    var message = parseParentMessage(event.data, now());
    if (!message) {
      return;
    }
    if (message.type === "ADAM_SSO_PROBE") {
      // 回报最新 pageId 状态
      sendState();
      return;
    }
    handleStart(message);
  }

  /** 每次 turbolinks:load 调用：生成新 pageId 并上报状态 */
  function onStableLoad() {
    if (!isStablePage(documentRef)) {
      return;
    }
    var pageId = generateUuid();
    if (pageId) {
      currentPageId = pageId;
    }
    sendState();
  }

  var started = false;
  function start() {
    if (started) {
      return;
    }
    started = true;
    windowRef.addEventListener("message", handleMessage, false);
  }

  return {
    onStableLoad: onStableLoad,
    start: start,
    handleMessage: handleMessage,
  };
}

/**
 * 创建浏览器桥实例：top-level、parentOrigin 缺失/非法、crypto 不可用时
 * 返回 no-op 实例
 */
function createBrowserInstance() {
  if (typeof window === "undefined" || window.self === window.top) {
    return NOOP_INSTANCE;
  }
  var parentOrigin = getConfiguredParentOrigin(document);
  if (!parentOrigin) {
    return NOOP_INSTANCE;
  }
  if (!generateUuid()) {
    return NOOP_INSTANCE;
  }
  var instance = createSsoAutologin({
    window: window,
    document: document,
    parentOrigin: parentOrigin,
  });
  instance.start();
  return instance;
}

/**
 * 浏览器入口
 */
function init() {
  var instance = null;
  return {
    onStableLoad: function () {
      if (!instance) {
        instance = createBrowserInstance();
      }
      instance.onStableLoad();
    },
  };
}

module.exports = {
  init: init,
  createSsoAutologin: createSsoAutologin,
  getConfiguredParentOrigin: getConfiguredParentOrigin,
  isStablePage: isStablePage,
  currentSafeReturnPath: currentSafeReturnPath,
  utf8ByteLength: utf8ByteLength,
  isValidUuid: isValidUuid,
  generateUuid: generateUuid,
  parseParentMessage: parseParentMessage,
  STORAGE_KEY: STORAGE_KEY,
  COOLDOWN_MS: COOLDOWN_MS,
  RETURN_PATH_MAX_BYTES: RETURN_PATH_MAX_BYTES,
};
